import { supabase } from "@/integrations/supabase/client";
import { withPermissionHandling } from "@/lib/permissionService";

export interface Client {
  id: string;
  name: string;
  country?: string | null;
  industry?: string | null;
  status: string;
  funnel_stage: string;
  logo_url?: string | null;
  org_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateClientData {
  name: string;
  country?: string;
  industry?: string;
  status?: string;
  funnel_stage?: string;
  logo_url?: string;
}

export interface UpdateClientData extends Partial<CreateClientData> {
  id: string;
}

export const clientService = {
  // Get all clients for the current organization
  getClients: async (): Promise<Client[]> => {
    return await withPermissionHandling(async () => {
      return await supabase
        .from('clients')
        .select('*')
        .order('updated_at', { ascending: false });
    });
  },

  // Get client by ID
  getClientById: async (id: string): Promise<Client | null> => {
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data;
  },

  // Create new client
  createClient: async (clientData: CreateClientData): Promise<Client> => {
    return await withPermissionHandling(async () => {
      // Remove org_id references - not needed anymore
      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
        .single();

      return await supabase
        .from('clients')
        .insert([clientData])
        .select()
        .single();
    });
  },

  // Update client
  updateClient: async ({ id, ...updateData }: UpdateClientData): Promise<Client> => {
    return await withPermissionHandling(async () => {
      return await supabase
        .from('clients')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
    });
  },

  // Delete client
  deleteClient: async (id: string): Promise<void> => {
    await withPermissionHandling(async () => {
      return await supabase
        .from('clients')
        .delete()
        .eq('id', id);
    });
  },

  // Get clients with statistics
  getClientsWithStats: async () => {
    const { data, error } = await supabase
      .from('clients')
      .select(`
        *,
        proposals:proposals(count),
        projects:projects(count),
        invoices:invoices(count)
      `);
    
    if (error) throw error;
    return data || [];
  }
};